import { useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { useSelector } from 'react-redux';
import LightbulbIcon from '@resources/icons/LightbulbIcon';
import DropdownItemSelectedIcon from '@resources/icons/DropdownItemSelectedIcon';
import { DefaultLightPresetsData } from '../constants';

const useStyles = makeStyles(() => ({
  root: {
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
  },
  lightsDropdown: {
    height: '41px',
    margin: '15.5px 0 0 21px',

    '& .MuiSelect-select': {
      padding: '10px',
      border: '1px solid #767676',
      borderRadius: '2px',
      backgroundColor: '#E2EAF3',
    },
  },
  dropdownValueWrapper: {
    display: 'flex',
    gap: '10px',
  },
  dropdownValueIcon: {
    color: '#334D6E',
    width: '13px',
    height: '18px',
    flexShrink: 0,
  },
  dropdownValue: {
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '21px',
    letterSpacing: '0.01em',
    color: '#102035',
  },
  dropdownPopover: {
    '& .MuiMenu-list': {
      padding: '0',
      width: '206px',
    },

    '& .MuiListItem-root': {
      textAlign: 'left',
      color: '#272D4E',
      fontSize: '15px',
      fontWeight: '500',
      lineHeight: '23px',
      letterSpacing: '0em',
      padding: '4.5px 16px',

      '&:hover': {
        backgroundColor: '#D6E0EE',
      },

      '& .MuiSvgIcon-root': {
        color: '#00C98F',
        marginLeft: '9px',
      },
    },
  },
}));

const LightPresetsController = ({
  name,
  object,
}) => {
  const classes = useStyles();
  const {
    control,
  } = useFormContext();
  const allEnvironments = useSelector((state) => state.scenes.environments.items);
  const currentEnvironment = useWatch({ control, name });

  const lightOptions = useMemo(() => {
    const result = [];

    DefaultLightPresetsData.forEach((item) => {
      const matchedEnvironment = allEnvironments.find((environment) => environment.uid === item.uid && environment.defaultContent === true);

      if (matchedEnvironment) {
        result.push({ ...matchedEnvironment, ...item });
      }
    });

    return result;
  }, [allEnvironments]);

  useEffect(() => {
    if (currentEnvironment?.intensity && object) {
      object.traverse((item) => {
        if (item.isMesh && item.material) {
          item.material.envMapIntensity = currentEnvironment.intensity;
          item.material.needUpdate = true;
        }
      });
    }
  }, [currentEnvironment, object]);

  return lightOptions.length ? (
    <div className={ classes.root }>
      <Controller
        name={ name }
        control={ control }
        render={ ({ field }) => (
          <Select
            { ...field }
            className={ classes.lightsDropdown }
            disableUnderline
            IconComponent={ () => null }
            MenuProps={ {
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              getContentAnchorEl: null,
              PopoverClasses: { paper: classes.dropdownPopover },
            } }
            renderValue={ (data) => (
              <div className={ classes.dropdownValueWrapper }>
                <LightbulbIcon className={ classes.dropdownValueIcon } size="small" />
                <span className={ classes.dropdownValue }>{data.name}</span>
              </div>
            ) }
          >
            {lightOptions.map((option, optionIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <MenuItem key={ `${option.id}-${optionIndex}` } value={ option }>
                {option.name}
                {field.value?.name === option.name ? (
                  <DropdownItemSelectedIcon />
                ) : null}
              </MenuItem>
            ))}
          </Select>
        ) }
      />
    </div>
  ) : null;
};

export default LightPresetsController;
